<template>
	<div
		class="bento-tile bento-tile--span-2 compute-activity"
		data-test="compute-activity-tile"
	>
		<div class="compute-activity__header">
			<div class="bento-tile__eyebrow">Compute activity</div>
			<div
				v-if="!ready"
				class="compute-activity__total"
				aria-hidden="true"
			>
				<SkeletonBlock
					class="compute-activity__count sgnl-text-color-default sgnl-h5-medium"
					width="var(--sgnl-unit-128)"
					radius="var(--sgnl-border-radius-4)"
				/>
				<SkeletonBlock
					class="compute-activity__period sgnl-text-color-secondary sgnl-paragraph-s-regular"
					width="var(--sgnl-unit-72)"
					radius="var(--sgnl-border-radius-4)"
				/>
			</div>
			<div
				v-else-if="empty"
				class="compute-activity__empty-title sgnl-text-color-secondary sgnl-paragraph-s-regular"
			>
				No activity yet
			</div>
			<div
				v-else
				class="compute-activity__total"
			>
				<span
					class="compute-activity__count sgnl-text-color-default sgnl-h5-medium"
					data-test="compute-activity-count"
					>{{ totalLast7.toLocaleString() }} tasks</span
				>
				<span class="compute-activity__period sgnl-text-color-secondary sgnl-paragraph-s-regular"
					>last 7 days</span
				>
			</div>
		</div>
		<div class="compute-activity__chart-group">
			<div
				v-if="!ready"
				class="compute-activity__histogram"
				aria-hidden="true"
			>
				<div
					v-for="i in 7"
					:key="i"
					class="compute-activity__bar-cell"
				>
					<div
						class="compute-activity__bar compute-activity__bar--skeleton"
						:style="{ height: skeletonBarHeight(i) + 'px' }"
					/>
				</div>
			</div>
			<div
				v-else-if="empty"
				class="compute-activity__placeholder sgnl-text-color-secondary"
				data-test="compute-activity-empty"
			>
				<ChartRing20 class="compute-activity__placeholder-icon" />
				<div class="sgnl-caption-regular">Activity will appear here after your first task</div>
				<v-btn
					variant="text"
					size="small"
					to="/launch"
					class="bento-tile__action"
				>
					Open Code Playground
					<ArrowRight16 />
				</v-btn>
			</div>
			<template v-else>
				<div
					class="compute-activity__histogram"
					data-test="compute-activity-histogram"
				>
					<div
						v-for="(d, i) in series"
						:key="i"
						class="compute-activity__bar-cell"
					>
						<span class="compute-activity__tooltip sgnl-text-color-inverse sgnl-footnote-regular">
							{{ d.value }} task{{ d.value === 1 ? "" : "s" }}
						</span>
						<div
							class="compute-activity__bar"
							:style="{ height: barHeight(d.value) + 'px' }"
						/>
					</div>
				</div>
			</template>
			<div
				v-if="ready && !empty"
				class="compute-activity__axis"
			>
				<span
					v-for="(d, i) in series"
					:key="i"
					class="compute-activity__axis-label sgnl-text-color-secondary sgnl-footnote-regular"
					>{{ d.label }}</span
				>
			</div>
			<div
				v-else
				class="compute-activity__axis"
				aria-hidden="true"
			>
				<span
					v-for="i in 7"
					:key="i"
					class="compute-activity__axis-label sgnl-text-color-secondary sgnl-footnote-regular"
					>&nbsp;</span
				>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import moment from "moment";
import ChartRing20 from "@carbon/icons-vue/es/chart--ring/20";
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";

import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";
import { mapState } from "pinia";
import { useTasksStore } from "@/stores/tasks";

type Point = { label: string; value: number };

// Histogram height in px, matches the .compute-activity__histogram height
// (--sgnl-unit-128) so the bar's height calculation stays in sync with CSS.
const HISTOGRAM_HEIGHT_PX = 128;
// Top padding inside the histogram so the tallest bar doesn't graze the tooltip.
const HISTOGRAM_BAR_TOP_PADDING_PX = 6;
// Floor so empty/near-zero days still show a visible nub.
const HISTOGRAM_MIN_BAR_PX = 2;

// Static skeleton bar heights (a gentle ascending curve, no animation) so the
// placeholder fills the histogram area at consistent dimensions to active state.
const SKELETON_BAR_FRACTIONS = [0.25, 0.45, 0.3, 0.6, 0.4, 0.75, 0.55];

export default defineComponent({
	name: "ComputeActivityTile",
	components: { ChartRing20, ArrowRight16, SkeletonBlock },
	props: {
		ready: {
			type: Boolean,
			default: true,
		},
	},
	computed: {
		...mapState(useTasksStore, { taskMetadataHistory: "taskMetadataHistory" }),
		series(): Point[] {
			const history = (this.taskMetadataHistory || []).slice(0, 7);
			return history
				.map((count, index) => ({
					label: moment().subtract(index, "days").format("MMM D"),
					value: count,
				}))
				.reverse();
		},
		totalLast7(): number {
			return this.series.reduce((sum, d) => sum + d.value, 0);
		},
		empty(): boolean {
			return this.totalLast7 === 0;
		},
		maxValue(): number {
			return Math.max(1, ...this.series.map((d) => d.value));
		},
	},
	methods: {
		barHeight(v: number): number {
			const usable = HISTOGRAM_HEIGHT_PX - HISTOGRAM_BAR_TOP_PADDING_PX;
			return Math.max(HISTOGRAM_MIN_BAR_PX, (v / this.maxValue) * usable);
		},
		skeletonBarHeight(i: number): number {
			const usable = HISTOGRAM_HEIGHT_PX - HISTOGRAM_BAR_TOP_PADDING_PX;
			return SKELETON_BAR_FRACTIONS[(i - 1) % SKELETON_BAR_FRACTIONS.length] * usable;
		},
	},
});
</script>

<style lang="scss" scoped>
.compute-activity__header {
	min-height: calc(var(--sgnl-unit-16) + var(--sgnl-space-4) + var(--sgnl-font-line-height-h5));
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-4);
}

.compute-activity__chart-group {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-6);
}

.compute-activity__total {
	display: flex;
	align-items: baseline;
	gap: var(--sgnl-space-8);
}

.compute-activity__placeholder {
	height: var(--sgnl-unit-128);
	border: var(--sgnl-border-width-1) dashed var(--sgnl-default-secondary-border);
	border-radius: var(--sgnl-border-radius-6);
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: var(--sgnl-space-6);
}

.compute-activity__placeholder-icon {
	color: var(--sgnl-default-secondary-icon);
}

.compute-activity__histogram {
	height: var(--sgnl-unit-128);
	display: flex;
	align-items: flex-end;
	gap: var(--sgnl-space-8);
	padding: 0 var(--sgnl-space-2);
}

.compute-activity__bar-cell {
	flex: 1;
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	align-items: center;
	min-width: 0;
	position: relative;
	cursor: default;
}

.compute-activity__bar {
	width: 100%;
	background: var(--sgnl-charts-chart-first);
	border-radius: var(--sgnl-border-radius-4) var(--sgnl-border-radius-4) 0 0;
	transition: background var(--sgnl-motion-duration-fast-02) ease;
}

.compute-activity__bar--skeleton {
	background: rgba(var(--v-theme-on-surface), var(--v-border-opacity));
}

.compute-activity__bar-cell:hover {
	background: var(--sgnl-default-secondary-background-mono);
}

.compute-activity__tooltip {
	position: absolute;
	top: var(--sgnl-space-neg-4);
	transform: translateY(-100%) scale(0.95);
	transform-origin: bottom center;
	background: var(--sgnl-default-primary-background-inverse);
	padding: var(--sgnl-space-4) var(--sgnl-space-8);
	border-radius: var(--sgnl-border-radius-6);
	white-space: nowrap;
	opacity: 0;
	pointer-events: none;
	transition:
		opacity var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-standard),
		transform var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-standard);
	box-shadow: 0 var(--sgnl-effect-4) var(--sgnl-effect-12) var(--opacity-black-b-black-20);
}

.compute-activity__bar-cell:hover .compute-activity__tooltip {
	opacity: 1;
	transform: translateY(-100%) scale(1);
}

.compute-activity__axis {
	display: flex;
	justify-content: space-between;
}

.compute-activity__axis-label {
	flex: 1;
	text-align: center;
	font-family: var(--sgnl-font-family-mono);
}
</style>
